import { supabase } from '@/lib/supabase';
import { getMainSiteUrl } from '@/lib/domain';

// ─────────────────────────────────────────────────────────────
// ZCall — socle de notre PROPRE système de visioconférence WebRTC.
//
// Ce module centralise :
//   • les types de réunion ;
//   • la construction du lien d'invitation unique ;
//   • le bloc-notes de réunion (persisté par participant → « enregistré dans
//     son compte » lorsqu'un utilisateur est connecté) ;
//   • les fichiers partagés d'une réunion (dossier collaboratif accessible à
//     tous les participants).
//
// ⚠️ ARCHITECTURE : le transport média repose sur WebRTC natif (P2P). Pour
// aller plus loin (≥ ~6 participants simultanés, ou LIVES à grande échelle),
// il faudra un serveur média (SFU : LiveKit / mediasoup) — la structure
// (`kind`, salles, notes, fichiers) est déjà posée pour le brancher sans
// réécrire l'application.
// ─────────────────────────────────────────────────────────────

export type ZCallKind = 'meeting' | 'live';

export interface ZCallMeeting {
  id: number;
  user_id: number;
  title: string;
  room_id: string;
  slug: string | null;
  scheduled_at: string | null;
  duration_minutes: number;
  status: 'scheduled' | 'active' | 'ended' | 'cancelled';
  kind?: string | null;
  created_at: string;
}

export interface ZCallNote {
  id: number;
  room_id: string;
  session_key: string;
  user_id: number | null;
  user_name: string | null;
  content: string;
  document_id: number | null;
  updated_at: string;
}

export interface ZCallFile {
  id: number;
  room_id: string;
  user_id: number | null;
  user_name: string | null;
  name: string;
  url: string;
  kind: string | null;
  size: number | null;
  created_at: string;
}

const TABLE_NOTES = 'zcall_notes';
const TABLE_FILES = 'zcall_files';

/** Identifiant de session du bloc-notes : utilisateur connecté, sinon appareil. */
export function buildSessionKey(userId: number | null | undefined, browserId: string): string {
  const uid = Number(userId);
  if (Number.isFinite(uid) && uid > 0) return `u${uid}`;
  return `guest-${browserId || 'anon'}`;
}

/** Charge le bloc-notes d'un participant pour une réunion (best-effort). */
export async function loadNote(roomId: string, sessionKey: string): Promise<ZCallNote | null> {
  try {
    const { data } = await supabase
      .from(TABLE_NOTES)
      .select('id, room_id, session_key, user_id, user_name, content, document_id, updated_at')
      .eq('room_id', roomId)
      .eq('session_key', sessionKey)
      .maybeSingle();
    return (data as ZCallNote) || null;
  } catch {
    return null;
  }
}

/** Enregistre le bloc-notes (upsert par room + session). Best-effort. */
export async function saveNote(
  roomId: string,
  sessionKey: string,
  payload: { userId: number | null; userName: string | null; content: string; documentId?: number | null },
): Promise<boolean> {
  try {
    const row: Record<string, unknown> = {
      room_id: roomId,
      session_key: sessionKey,
      user_id: payload.userId,
      user_name: payload.userName,
      content: payload.content,
      updated_at: new Date().toISOString(),
    };
    if (payload.documentId !== undefined) row.document_id = payload.documentId;
    const { error } = await supabase.from(TABLE_NOTES).upsert(row, { onConflict: 'room_id,session_key' });
    return !error;
  } catch {
    return false;
  }
}

/** Liste les fichiers partagés d'une réunion. */
export async function listRoomFiles(roomId: string): Promise<ZCallFile[]> {
  try {
    const { data } = await supabase
      .from(TABLE_FILES)
      .select('id, room_id, user_id, user_name, name, url, kind, size, created_at')
      .eq('room_id', roomId)
      .order('created_at', { ascending: false })
      .limit(200);
    return (data as ZCallFile[]) || [];
  } catch {
    return [];
  }
}

/** Ajoute un fichier au dossier partagé de la réunion. */
export async function addRoomFile(
  roomId: string,
  payload: { userId: number | null; userName: string | null; name: string; url: string; kind: string; size?: number },
): Promise<ZCallFile | null> {
  try {
    const { data, error } = await supabase
      .from(TABLE_FILES)
      .insert({
        room_id: roomId,
        user_id: payload.userId,
        user_name: payload.userName,
        name: payload.name,
        url: payload.url,
        kind: payload.kind,
        size: payload.size ?? null,
      })
      .select('id, room_id, user_id, user_name, name, url, kind, size, created_at')
      .maybeSingle();
    if (error) throw error;
    return (data as ZCallFile) || null;
  } catch {
    return null;
  }
}

/** Supprime un fichier partagé (par son auteur). */
export async function deleteRoomFile(fileId: number): Promise<boolean> {
  try {
    const { error } = await supabase.from(TABLE_FILES).delete().eq('id', fileId);
    return !error;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
// Enregistrements de réunion (table zcall_recordings).
//
// Chaque enregistrement produit dans une salle ZCall est déposé dans le
// dossier « ZCall » de Mes médias (/dashboard/media) ET son historique
// (durée, taille) est conservé ici pour l'affichage.
// ─────────────────────────────────────────────────────────────

export interface ZCallRecording {
  id: number;
  user_id: number | null;
  room_id: string | null;
  meeting_id: number | null;
  title: string | null;
  url: string;
  storage_key: string | null;
  duration_seconds: number;
  size_bytes: number;
  created_at: string;
}

/** Liste l'historique des enregistrements ZCall d'un utilisateur. */
export async function listRecordings(userId: number, limit = 200): Promise<ZCallRecording[]> {
  try {
    const { data } = await supabase
      .from('zcall_recordings')
      .select('id, user_id, room_id, meeting_id, title, url, storage_key, duration_seconds, size_bytes, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return (data as ZCallRecording[]) || [];
  } catch {
    return [];
  }
}

/** Supprime une ligne d'historique d'enregistrement. */
export async function deleteRecording(id: number): Promise<boolean> {
  try {
    const { error } = await supabase.from('zcall_recordings').delete().eq('id', id);
    return !error;
  } catch {
    return false;
  }
}

/** Formate une durée en secondes → « 12:34 » ou « 1:02:34 ». */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

// ─────────────────────────────────────────────────────────────
// Statistiques de spectateurs des LIVES (table zcall_live_views).
// ─────────────────────────────────────────────────────────────

/** Début de session d'un spectateur de live : renvoie l'id de la vue. */
export async function startLiveView(
  meetingId: number | null,
  roomId: string,
  userId: number | null,
  viewerKey: string,
): Promise<number | null> {
  if (!roomId) return null;
  try {
    const { data } = await supabase
      .from('zcall_live_views')
      .insert({ meeting_id: meetingId, room_id: roomId, user_id: userId, viewer_key: viewerKey })
      .select('id')
      .maybeSingle();
    return data?.id ? Number(data.id) : null;
  } catch {
    return null;
  }
}

/** Fin de session d'un spectateur : enregistre le temps de visionnage. */
export async function endLiveView(viewId: number | null, watchSeconds: number): Promise<void> {
  if (!viewId) return;
  try {
    await supabase.from('zcall_live_views').update({ watch_seconds: Math.max(0, Math.round(watchSeconds)) }).eq('id', viewId);
  } catch {
    /* best-effort */
  }
}

/** Enregistre le pic de spectateurs simultanés atteint pour un live. */
export async function updateLivePeak(meetingId: number, peak: number): Promise<void> {
  if (!meetingId || peak <= 0) return;
  try {
    const { data } = await supabase.from('zcall_meetings').select('live_peak_viewers').eq('id', meetingId).maybeSingle();
    const current = Number(data?.live_peak_viewers) || 0;
    if (peak > current) await supabase.from('zcall_meetings').update({ live_peak_viewers: peak }).eq('id', meetingId);
  } catch {
    /* best-effort */
  }
}

export interface ZCallLiveView {
  id: number;
  meeting_id: number | null;
  room_id: string | null;
  user_id: number | null;
  viewer_key: string | null;
  watch_seconds: number;
  joined_at: string;
}

/** Liste les diffusions (lives) d'un utilisateur. */
export async function listLiveMeetings(userId: number): Promise<ZCallMeeting[]> {
  try {
    const { data } = await supabase
      .from('zcall_meetings')
      .select('id, user_id, title, room_id, slug, scheduled_at, duration_minutes, status, kind, created_at')
      .eq('user_id', userId)
      .eq('kind', 'live')
      .order('created_at', { ascending: false });
    return (data as ZCallMeeting[]) || [];
  } catch {
    return [];
  }
}

/** Liste les sessions de spectateurs pour un ensemble de lives. */
export async function listLiveViews(meetingIds: number[]): Promise<ZCallLiveView[]> {
  if (!meetingIds.length) return [];
  try {
    const { data } = await supabase
      .from('zcall_live_views')
      .select('id, meeting_id, room_id, user_id, viewer_key, watch_seconds, joined_at')
      .in('meeting_id', meetingIds);
    return (data as ZCallLiveView[]) || [];
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────────────────
// Partage public de documents / dossiers (table zcall_shares).
//
// Un partage est un INSTANTANÉ du contenu au moment de la création : il est
// donc consultable par n'importe qui possédant le lien (avec mot de passe
// facultatif) SANS compte Zifek, sans dépendre des règles d'accès de la table
// `documents`.
// ─────────────────────────────────────────────────────────────

export interface ShareItem {
  name: string;
  type: string;
  html?: string;
  pdfBase64?: string;
  /** Diaporama interactif (présentations partagées en consultation seule). */
  deck?: unknown;
  /** Contenu brut du document (feuilles, diapositives…) pour un aperçu fidèle. */
  content?: Record<string, unknown>;
}

export interface ShareRow {
  id: number;
  user_id: number | null;
  token: string;
  title: string | null;
  password_hash: string | null;
  content: ShareItem[];
  kind?: string | null;
  view_count?: number | null;
  download_count?: number | null;
  created_at?: string | null;
}

/** Jeton de partage unique et imprévisible (cryptographique si disponible). */
export function generateShareToken(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
  const bytes = new Uint8Array(16);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

/** Crée un partage public et renvoie son jeton. */
export async function createShare(
  userId: number | null,
  title: string,
  items: ShareItem[],
  passwordHash: string | null,
  kind: string = 'docs',
): Promise<{ id: number; token: string; url: string } | null> {
  try {
    const token = generateShareToken();
    const { data, error } = await supabase
      .from('zcall_shares')
      .insert({
        user_id: userId,
        token,
        title,
        password_hash: passwordHash,
        content: items,
        kind,
        view_count: 0,
        download_count: 0,
      })
      .select('id')
      .maybeSingle();
    if (error) throw error;
    const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '').replace(/\/$/, '');
    return { id: Number(data?.id) || 0, token, url: `${basePath}/partage/${token}` };
  } catch {
    return null;
  }
}

/** URL absolue (domaine principal + chemin de consultation publique). */
export function shareAbsoluteUrl(token: string): string {
  const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '').replace(/\/$/, '');
  const site = getMainSiteUrl();
  return `${site}${basePath}/preview/${token}`;
}

/** Liste les liens de partage créés par un marchand (du plus récent au plus ancien). */
export async function listShares(userId: number): Promise<ShareRow[]> {
  if (!userId) return [];
  try {
    const { data } = await supabase
      .from('zcall_shares')
      .select('id, user_id, token, title, password_hash, content, kind, view_count, download_count, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);
    return (data as ShareRow[]) || [];
  } catch {
    return [];
  }
}

/** Supprime un lien de partage. */
export async function deleteShare(id: number): Promise<void> {
  await supabase.from('zcall_shares').delete().eq('id', id);
}

/** Incrémente le compteur de consultations d'un partage (best-effort). */
export async function incrementShareView(id: number): Promise<void> {
  if (!id) return;
  try {
    const { data } = await supabase.from('zcall_shares').select('view_count').eq('id', id).maybeSingle();
    const current = Number(data?.view_count) || 0;
    await supabase.from('zcall_shares').update({ view_count: current + 1 }).eq('id', id);
  } catch {
    /* best-effort */
  }
}

/** Incrémente le compteur de téléchargements d'un partage (best-effort). */
export async function incrementShareDownload(id: number): Promise<void> {
  if (!id) return;
  try {
    const { data } = await supabase.from('zcall_shares').select('download_count').eq('id', id).maybeSingle();
    const current = Number(data?.download_count) || 0;
    await supabase.from('zcall_shares').update({ download_count: current + 1 }).eq('id', id);
  } catch {
    /* best-effort */
  }
}

/** Charge un partage public par son jeton. */
export async function getShareByToken(token: string): Promise<ShareRow | null> {
  try {
    const { data } = await supabase
      .from('zcall_shares')
      .select('id, user_id, token, title, password_hash, content, kind, view_count, download_count, created_at')
      .eq('token', token)
      .maybeSingle();
    return (data as ShareRow) || null;
  } catch {
    return null;
  }
}

/**
 * Construit l'URL d'invitation publique d'une réunion.
 *
 * Le lien est bâti sur le DOMAINE PRINCIPAL de la plateforme
 * (ex: https://zifek.fr/zcall/xxx) et non sur le sous-domaine de la boutique :
 * sur le domaine principal la salle s'ouvre directement, alors qu'un
 * sous-domaine boutique déclenche le chargement de toute la vitrine — ce qui
 * provoquait un « site inaccessible / délai dépassé » pour les invités.
 */
export function getMeetingUrl(
  _inviteDomain: string,
  meeting: { slug: string | null; room_id: string },
): string {
  const basePath = (typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__ : '').replace(/\/$/, '');
  const segment = meeting.slug || meeting.room_id;
  const path = meeting.slug ? 'zcall' : 'call';
  return `${getMainSiteUrl()}${basePath}/${path}/${segment}`;
}